import Head from 'next/head';
import { generateNextSeo } from 'next-seo/pages';

import { useEnvironment } from '#cutils/context/EnvironmentContext';
import { notNullOrUndefined } from '#cutils/misc/notNullOrUndefined';
import { buildArticleSeoConfig } from '#cutils/seo/buildArticleSeoConfig';
import { buildArticleStructuredData } from '#cutils/seo/schema-org/buildArticleStructuredData';
import type { ArticleData } from '#shared/types/article';
import { PageView, Tracktor } from 'react-tracktor';

import { SchemaOrg } from '../SchemaOrg';

import styles from './Article.module.scss';
import { ArticleBody } from './ArticleBody';
import { ArticleFooter } from './ArticleFooter';
import { ArticleHeader } from './ArticleHeader';
import { EbuOverlay } from './EbuOverlay';

type Props = {
	article: ArticleData;
};

export function Article({ article }: Props) {
	const {
		constants: { appUrl, SELF_ORIGIN },
	} = useEnvironment();

	const mappedMetaArticleTags = (article.tags2?.nodes || [])
		.filter(notNullOrUndefined)
		.map((tag) => <meta property="article:tag" content={tag.text} key={tag.text} />);

	const seoConfig = buildArticleSeoConfig(article, SELF_ORIGIN);
	const structuredData = buildArticleStructuredData(article, appUrl, SELF_ORIGIN);

	return (
		<>
			{/* Injects dynamic meta tags into the `<head />`. */}
			<Head>
				<meta property="article:published_time" content={article.publicationDate ?? undefined} />
				<meta property="article:publisher" content={appUrl} />

				<meta name="apple-itunes-app" content="app-id=998904798" />

				{mappedMetaArticleTags}

				{generateNextSeo(seoConfig)}
			</Head>

			<PageView
				key={article.shareUrl}
				pageViewData={{ data: article.trackingInfos || {}, event: { page: article.shareUrl, type: 'PageViewEvent' } }}
			/>

			<Tracktor trackingData={{ data: article.trackingInfos || {}, event: { page: article.shareUrl } }}>
				<article key={article.rowId} className={styles.article}>
					<EbuOverlay articleId={article.rowId} />
					<ArticleHeader article={article} bodyClassName={styles.section} />
					<ArticleBody article={article} className={styles.section} />
					<ArticleFooter article={article} bodyClassName={styles.section} />
					{/* Injects script tags with structured data. */}
					<SchemaOrg schema={structuredData} />
				</article>
			</Tracktor>
		</>
	);
}
